import { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  Search,
  Save,
  RefreshCw,
  Building2,
  Users,
  BadgeCheck,
  BadgeX,
  CheckSquare,
  Square,
  PencilLine,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

// ✅ set your base url correctly
const BASE_URL = "https://nissi-india.com"; // change if needed

const EmployeePromotion = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade, lightColor, lightShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700`;
  const accentBtnClass = `bg-${lightColor}-${lightShade}`;

  const finYearId = useMemo(
    () => Number(localStorage.getItem("finYearId") || 0),
    [],
  );
  const adminId = useMemo(
    () => Number(localStorage.getItem("userId") || 0),
    [],
  );

  const [showFilters, setShowFilters] = useState(true);

  const [branchId, setBranchId] = useState("Select");
  const [branches, setBranches] = useState([]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);

  // selection + paging
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [page, setPage] = useState(1);
  const limit = 10;

  const totalPages = Math.max(1, Math.ceil(rows.length / limit));
  const pageRows = rows.slice((page - 1) * limit, page * limit);

  const inputClass = `w-full px-3 py-2 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const selectClass = `w-full px-3 py-2 border rounded-xl ${
    isDark
      ? "bg-gray-800 border-gray-700 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const cardClass = `p-6 mt-6 rounded-3xl shadow-xl border ${
    isDark
      ? "bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md border-white/10 text-white"
      : `${currentSkin.glass} bg-white text-black border-gray-200`
  }`;

  const cardBase = `rounded-2xl border ${
    isDark ? "border-white/10 bg-white/5" : "border-gray-200 bg-gray-50"
  }`;

  const ghostBtn =
    `inline-flex items-center gap-2 px-4 py-2 rounded-2xl border ` +
    (isDark
      ? "border-white/10 bg-white/5 hover:bg-white/10"
      : "border-gray-200 bg-white hover:bg-gray-50");

  const getPageItems = (current, total) => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    const left = Math.max(2, current - delta);
    const right = Math.min(total - 1, current + delta);

    range.push(1);
    for (let i = left; i <= right; i++) range.push(i);
    if (total > 1) range.push(total);

    const unique = [...new Set(range)].sort((a, b) => a - b);

    for (let i = 0; i < unique.length; i++) {
      const pageNum = unique[i];
      const prev = unique[i - 1];
      if (i > 0 && pageNum - prev > 1) rangeWithDots.push("ellipsis");
      rangeWithDots.push(pageNum);
    }
    return rangeWithDots;
  };

  const normalizeBranches = (raw) => {
    return (raw || []).map((x) => ({
      id:
        x.fld_Branchid ??
        x.branchid ??
        x.fld_branchId ??
        x.BranchId ??
        x.id ??
        x.value ??
        "",
      name:
        x.fld_BranchName ??
        x.branchname ??
        x.fld_branchName ??
        x.BranchName ??
        x.name ??
        x.text ??
        "",
    }));
  };

  const mapGridRow = (r, idx) => {
    const employeeId =
      r.fld_Employeeid ?? r.fld_employeeid ?? r.EmployeeId ?? r.employeeId ?? 0;
    return {
      _key: `${employeeId}-${idx}`,
      employeeId: Number(employeeId) || 0,

      branchId: r.fld_branchid ?? r.fld_branchId ?? r.branchId ?? 0,
      branchName: r.fld_branchname ?? "",

      empName: r.fld_Employee ?? r.fld_employee ?? "",
      preEmpCode: r.fld_preEmployeecode ?? "",
      empCode: r.fld_Employeecode ?? "",

      preDeptId: Number(r.fld_deptid || 0),
      preDeptName: r.fld_predepartmentname ?? "",
      deptName: r.fld_departmentname ?? "",

      preDesigId: Number(r.fld_desigid || 0),
      preDesigName: r.fld_predesignationname ?? "",
      desigName: r.fld_designationname ?? "",

      // editable fields
      newEmpCode: "",
      deptChange: false,
      newDeptId: 0,
      desigChange: false,
      newDesigId: 0,
    };
  };

  const loadBranches = async () => {
    try {
      if (!finYearId) return;
      const res = await axios.get(
        `${BASE_URL}/api/employee-promotion/branches`,
        {
          params: { finYearId },
        },
      );
      if (res?.data?.status) {
        const list = normalizeBranches(res.data.data);
        setBranches(list);
        const savedBranch = localStorage.getItem("branchId");
        if (savedBranch) setBranchId(savedBranch);
      } else {
        toast.error(res?.data?.message || "Failed to load branches");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load branches",
      );
    }
  };

  const loadDeptAndDesig = async () => {
    try {
      if (!finYearId) return;

      const [depRes, desRes] = await Promise.all([
        axios.get(`${BASE_URL}/api/employee-promotion/departments`, {
          params: { finYearId },
        }),
        axios.get(`${BASE_URL}/api/employee-promotion/designations`, {
          params: { finYearId },
        }),
      ]);

      if (depRes?.data?.status) setDepartments(depRes.data.data || []);
      if (desRes?.data?.status) setDesignations(desRes.data.data || []);
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load masters",
      );
    }
  };

  const loadGrid = async () => {
    if (!finYearId) {
      toast.warn("Please select Financial Year (login again).");
      return;
    }
    if (!branchId || branchId === "Select") {
      toast.warn("Please select Branch.");
      return;
    }

    try {
      setLoading(true);
      setSelectedIds([]);
      setSelectAll(false);
      setPage(1);

      localStorage.setItem("branchId", branchId);

      const res = await axios.get(`${BASE_URL}/api/employee-promotion/list`, {
        params: { finYearId, branchId },
      });

      if (res?.data?.status) {
        const data = (res.data.data || []).map(mapGridRow);
        setRows(data);
      } else {
        toast.error(res?.data?.message || "No records found");
        setRows([]);
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || "Failed to load list",
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBranches();
    loadDeptAndDesig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ Clear filter back to "Select" and clear the currently loaded grid
  const handleClearFilters = () => {
    setBranchId("Select");
    setRows([]);
    setSelectedIds([]);
    setSelectAll(false);
    setPage(1);
  };

  const toggleSelect = (_key) => {
    setSelectedIds((prev) =>
      prev.includes(_key) ? prev.filter((x) => x !== _key) : [...prev, _key],
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
      setSelectAll(false);
      return;
    }
    const keys = pageRows.map((r) => r._key);
    setSelectedIds(keys);
    setSelectAll(true);
  };

  useEffect(() => {
    const keys = pageRows.map((r) => r._key);
    const allSelected =
      keys.length > 0 && keys.every((k) => selectedIds.includes(k));
    setSelectAll(allSelected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rows, selectedIds]);

  const updateRow = (_key, patch) => {
    setRows((prev) =>
      prev.map((r) => (r._key === _key ? { ...r, ...patch } : r)),
    );
  };

  const submitSelected = async () => {
    if (!finYearId) {
      toast.warn("Please select Financial Year (login again).");
      return;
    }
    if (!adminId) {
      toast.warn("User not found (login again).");
      return;
    }
    if (selectedIds.length === 0) {
      toast.warn("Please select at least one row.");
      return;
    }

    const picked = rows.filter((r) => selectedIds.includes(r._key));

    const items = picked
      .map((r) => {
        const hasEmpCode = String(r.newEmpCode || "").trim().length > 0;
        const hasDept = r.deptChange && Number(r.newDeptId) > 0;
        const hasDes = r.desigChange && Number(r.newDesigId) > 0;

        if (!hasEmpCode && !hasDept && !hasDes) return null;

        return {
          employeeId: r.employeeId,
          preEmpCode: r.empCode,
          newEmpCode: hasEmpCode ? String(r.newEmpCode).trim() : "",
          preDeptId: r.preDeptId,
          newDeptId: hasDept ? Number(r.newDeptId) : 0,
          preDesigId: r.preDesigId,
          newDesigId: hasDes ? Number(r.newDesigId) : 0,
        };
      })
      .filter(Boolean);

    if (items.length === 0) {
      toast.warn("Selected rows have no changes to submit.");
      return;
    }

    const ok = window.confirm("Are you sure to submit?");
    if (!ok) return;

    try {
      setLoading(true);
      const res = await axios.post(
        `${BASE_URL}/api/employee-promotion/submit`,
        {
          finYearId,
          userId: adminId,
          items,
        },
      );

      if (res?.data?.status) {
        toast.success(res?.data?.message || "Saved Successfully.");
        await loadGrid();
      } else {
        toast.error(res?.data?.message || "Save failed.");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || "Save failed.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <PayRollLayoutWrapper>
      {/* ✅ toast outside the card */}
      <ToastContainer
        position="top-right"
        autoClose={2500}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div
        className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"} `}
      >
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">EMPLOYEE PROMOTION</span>
          }
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={`${cardClass}`}>
          {/* ==================== HEADER ==================== */}
          <div className="flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between mb-6">
            <div className="flex items-center gap-3">
              <div
                className={`p-3 rounded-2xl ${isDark ? "bg-white/10" : "bg-gray-100"}`}
              >
                <Users className="w-6 h-6" />
              </div>
              <div>
                <div className="text-2xl font-bold">
                  Promotion / Dept-Desig-EmpCode Change
                </div>
                <div
                  className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}
                >
                  Branch-wise upgrade screen
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={submitSelected}
                disabled={loading || selectedIds.length === 0}
                className={`inline-flex items-center gap-2 px-5 py-2 rounded-2xl text-white shadow-lg ${
                  loading || selectedIds.length === 0
                    ? "bg-gray-400 cursor-not-allowed"
                    : `${primaryBtnClass}`
                }`}
              >
                <Save className="w-4 h-4" />
                Save Selected
              </button>

              <button
                onClick={() => setShowFilters((v) => !v)}
                className={ghostBtn}
              >
                {showFilters ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* ==================== SEARCH FILTERS ==================== */}
          {showFilters && (
            <div className={`${cardBase} p-5`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div className="text-lg font-extrabold">Search Criteria</div>

                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={handleClearFilters}
                    className={ghostBtn}
                    disabled={loading}
                  >
                    Clear
                  </button>

                  <button
                    onClick={loadGrid}
                    className={ghostBtn}
                    disabled={loading}
                  >
                    <RefreshCw
                      className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
                    />
                    Refresh
                  </button>

                  <button
                    onClick={loadGrid}
                    disabled={loading}
                    className={`inline-flex items-center gap-2 px-5 py-2 rounded-2xl text-white shadow-lg ${
                      loading
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-gray-600 hover:bg-gray-700"
                    }`}
                  >
                    <Search className="w-4 h-4" />
                    Search
                  </button>
                </div>
              </div>

              <div>
                <label
                  className={`text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`}
                >
                  Branch <span className="text-red-500">*</span>
                </label>
                <div className="mt-2 flex items-center gap-2 max-w-md">
                  <Building2 className="w-4 h-4 opacity-70" />
                  <select
                    value={branchId}
                    onChange={(e) => setBranchId(e.target.value)}
                    className={selectClass}
                  >
                    <option value="Select">Select</option>
                    {branches.map((b) => (
                      <option key={String(b.id)} value={String(b.id)}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* ==================== RESULTS TABLE ==================== */}
          <div
            className={`mt-6 rounded-3xl border ${isDark ? "border-white/10" : "border-gray-200"}`}
          >
            <div className="w-full overflow-x-auto rounded-3xl">
              <div className="min-w-full w-max">
                <table className="min-w-full table-auto">
                  <thead>
                    <tr>
                      <th
                        className={`px-4 py-4 text-center font-bold text-white whitespace-nowrap ${primaryBtnClass}`}
                      >
                        S.No
                      </th>
                      <th
                        className={`px-4 py-4 text-left font-bold text-white whitespace-nowrap ${primaryBtnClass}`}
                      >
                        Location
                      </th>
                      <th
                        className={`px-4 py-4 text-left font-bold text-white whitespace-nowrap ${primaryBtnClass}`}
                      >
                        Employee
                      </th>
                      <th
                        className={`px-4 py-4 text-center font-bold text-white whitespace-nowrap ${primaryBtnClass}`}
                      >
                        Pre Code
                      </th>
                      <th
                        className={`px-4 py-4 text-center font-bold text-white whitespace-nowrap ${primaryBtnClass}`}
                      >
                        Current Code
                      </th>
                      <th
                        className={`px-4 py-4 text-center font-bold text-white whitespace-nowrap ${primaryBtnClass}`}
                      >
                        New Code
                      </th>
                      <th
                        className={`px-4 py-4 text-left font-bold text-white whitespace-nowrap ${primaryBtnClass}`}
                      >
                        Department
                      </th>
                      <th
                        className={`px-4 py-4 text-center font-bold text-white whitespace-nowrap ${primaryBtnClass}`}
                      >
                        Dept Change
                      </th>
                      <th
                        className={`px-4 py-4 text-left font-bold text-white whitespace-nowrap ${primaryBtnClass}`}
                      >
                        New Dept
                      </th>
                      <th
                        className={`px-4 py-4 text-left font-bold text-white whitespace-nowrap ${primaryBtnClass}`}
                      >
                        Designation
                      </th>
                      <th
                        className={`px-4 py-4 text-center font-bold text-white whitespace-nowrap ${primaryBtnClass}`}
                      >
                        Desig Change
                      </th>
                      <th
                        className={`px-4 py-4 text-left font-bold text-white whitespace-nowrap ${primaryBtnClass}`}
                      >
                        New Desig
                      </th>
                      <th
                        className={`px-4 py-4 text-center font-bold text-white whitespace-nowrap ${primaryBtnClass}`}
                      >
                        <div className="flex items-center justify-center gap-2">
                          Select All
                          <button
                            type="button"
                            onClick={handleSelectAll}
                            className="p-1 rounded-lg bg-white/15 hover:bg-white/25"
                            title="Select current page"
                          >
                            {selectAll ? (
                              <CheckSquare className="w-4 h-4" />
                            ) : (
                              <Square className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </th>
                    </tr>
                  </thead>

                  <tbody
                    className={`${isDark ? "bg-gray-900" : "bg-white"} divide-y ${isDark ? "divide-white/10" : "divide-gray-200"}`}
                  >
                    {loading ? (
                      Array.from({ length: limit }).map((_, i) => (
                        <tr key={i}>
                          <td className="px-4 py-4" colSpan={13}>
                            <div className="h-4 rounded bg-gray-300/40 dark:bg-white/10 animate-pulse" />
                          </td>
                        </tr>
                      ))
                    ) : pageRows.length === 0 ? (
                      <tr>
                        <td colSpan={13} className="px-4 py-12 text-center">
                          <div className="inline-flex items-center gap-2">
                            <BadgeX className="w-5 h-5 opacity-70" />
                            <span
                              className={`${isDark ? "text-gray-300" : "text-gray-600"}`}
                            >
                              No Records Found
                            </span>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      pageRows.map((r, idx) => {
                        const checked = selectedIds.includes(r._key);
                        return (
                          <tr
                            key={r._key}
                            className={`${isDark ? "hover:bg-white/5" : "hover:bg-gray-50"} transition`}
                          >
                            <td className="px-4 py-3 text-center">
                              {(page - 1) * limit + idx + 1}
                            </td>
                            <td className="px-4 py-3">{r.branchName}</td>
                            <td className="px-4 py-3">
                              <div className="font-semibold">{r.empName}</div>
                              <div
                                className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}
                              >
                                EmpId: {r.employeeId}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center">
                              {r.preEmpCode}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {r.empCode}
                            </td>

                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <PencilLine className="w-4 h-4 opacity-70" />
                                <input
                                  className={inputClass}
                                  value={r.newEmpCode}
                                  onChange={(e) =>
                                    updateRow(r._key, {
                                      newEmpCode: e.target.value,
                                    })
                                  }
                                  placeholder="New code"
                                />
                              </div>
                            </td>

                            <td className="px-4 py-3">
                              <div className="font-medium">{r.deptName}</div>
                              <div
                                className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}
                              >
                                DeptId: {r.preDeptId}
                              </div>
                            </td>

                            <td className="px-4 py-3 text-center">
                              <button
                                type="button"
                                onClick={() => {
                                  const next = !r.deptChange;
                                  updateRow(r._key, {
                                    deptChange: next,
                                    newDeptId: next ? r.newDeptId : 0,
                                    ...(next
                                      ? {}
                                      : { desigChange: false, newDesigId: 0 }),
                                  });
                                }}
                                className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border ${
                                  r.deptChange
                                    ? "border-green-500/40 bg-green-500/10"
                                    : isDark
                                      ? "border-white/10 bg-white/5"
                                      : "border-gray-200 bg-gray-50"
                                }`}
                              >
                                {r.deptChange ? (
                                  <BadgeCheck className="w-4 h-4 text-green-500" />
                                ) : (
                                  <BadgeX className="w-4 h-4 opacity-60" />
                                )}
                                <span className="text-xs font-semibold">
                                  {r.deptChange ? "YES" : "NO"}
                                </span>
                              </button>
                            </td>

                            <td className="px-4 py-3">
                              <select
                                disabled={!r.deptChange}
                                className={`${selectClass} ${!r.deptChange ? "opacity-50 cursor-not-allowed" : ""}`}
                                value={String(r.newDeptId || 0)}
                                onChange={(e) => {
                                  updateRow(r._key, {
                                    newDeptId: Number(e.target.value),
                                    desigChange: false,
                                    newDesigId: 0,
                                  });
                                }}
                              >
                                <option value="0">Select</option>
                                {departments.map((d) => (
                                  <option
                                    key={String(d.deptId)}
                                    value={String(d.deptId)}
                                  >
                                    {d.deptName}
                                  </option>
                                ))}
                              </select>
                            </td>

                            <td className="px-4 py-3">
                              <div className="font-medium">{r.desigName}</div>
                              <div
                                className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}
                              >
                                DesigId: {r.preDesigId}
                              </div>
                            </td>

                            <td className="px-4 py-3 text-center">
                              <button
                                type="button"
                                onClick={() => {
                                  const next = !r.desigChange;
                                  updateRow(r._key, {
                                    desigChange: next,
                                    newDesigId: next ? r.newDesigId : 0,
                                  });
                                }}
                                className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border ${
                                  r.desigChange
                                    ? "border-blue-500/40 bg-blue-500/10"
                                    : isDark
                                      ? "border-white/10 bg-white/5"
                                      : "border-gray-200 bg-gray-50"
                                }`}
                              >
                                {r.desigChange ? (
                                  <BadgeCheck className="w-4 h-4 text-blue-500" />
                                ) : (
                                  <BadgeX className="w-4 h-4 opacity-60" />
                                )}
                                <span className="text-xs font-semibold">
                                  {r.desigChange ? "YES" : "NO"}
                                </span>
                              </button>
                            </td>

                            <td className="px-4 py-3">
                              <select
                                disabled={!r.desigChange}
                                className={`${selectClass} ${!r.desigChange ? "opacity-50 cursor-not-allowed" : ""}`}
                                value={String(r.newDesigId || 0)}
                                onChange={(e) =>
                                  updateRow(r._key, {
                                    newDesigId: Number(e.target.value),
                                  })
                                }
                              >
                                <option value="0">Select</option>
                                {designations.map((d) => (
                                  <option
                                    key={String(d.desigId)}
                                    value={String(d.desigId)}
                                  >
                                    {d.desigName}
                                  </option>
                                ))}
                              </select>
                            </td>

                            <td className="px-4 py-3 text-center">
                              <button
                                type="button"
                                onClick={() => toggleSelect(r._key)}
                                className={`p-2 rounded-xl border ${
                                  checked
                                    ? "border-emerald-500/40 bg-emerald-500/10"
                                    : isDark
                                      ? "border-white/10 bg-white/5"
                                      : "border-gray-200 bg-gray-50"
                                }`}
                                title="Select row"
                              >
                                {checked ? (
                                  <CheckSquare className="w-5 h-5 text-emerald-500" />
                                ) : (
                                  <Square className="w-5 h-5 opacity-70" />
                                )}
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* ==================== PAGINATION ==================== */}
          <div
            className={`flex flex-col md:flex-row items-center justify-between mt-6 gap-4 ${cardBase} p-4`}
          >
            <div
              className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}
            >
              Showing{" "}
              <span className="font-semibold">
                {rows.length === 0 ? 0 : (page - 1) * limit + 1}
              </span>{" "}
              to{" "}
              <span className="font-semibold">
                {Math.min(page * limit, rows.length)}
              </span>{" "}
              of <span className="font-semibold">{rows.length}</span> results
            </div>

            {totalPages > 1 && (
              <div className="inline-flex rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === 1
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  Prev
                </button>

                {getPageItems(page, totalPages).map((item, idx) => {
                  if (item === "ellipsis") {
                    return (
                      <span
                        key={`dots-${idx}`}
                        className={`px-4 py-2 text-sm font-bold ${isDark ? "text-gray-400" : "text-gray-500"}`}
                      >
                        …
                      </span>
                    );
                  }

                  const pno = item;
                  const active = page === pno;

                  return (
                    <button
                      key={pno}
                      onClick={() => setPage(pno)}
                      className={`px-4 py-2 text-sm font-bold transition ${
                        active
                          ? `${primaryBtnClass} text-white`
                          : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                      }`}
                    >
                      {pno}
                    </button>
                  );
                })}

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === totalPages
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  Next
                </button>
              </div>
            )}
          </div>
          {/* small note */}
          <div
            className={`mt-5 text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}
          >
            Note: Save Selected will submit only rows you selected and only
            fields you changed (like ASP logic).
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default EmployeePromotion;
